import environment from '../environment';
import { ActionStatus } from './action-status'
import { html, TemplateResult } from 'lit';
import { repeatThis } from '../utils/util';
import {
  FREE_TRIAL_CONVERTED_MSG,
  FREE_TRIAL_INTL_CONVERTED_MSG,
} from '../../features/free-trial/interfaces/free-trial-conversion.interface';
import { captureUserLoginFromAlertAnalytics, getLoginURLWithRedirectUrlToCurrentPage } from '../utils/login-utils';

/*----- DECLARE INTERFACES ------*/
export declare interface ApiState {
    data: any;
    status: ActionStatus;
    error?: any;
  }

export const defaultApiState: ApiState = {
    data: undefined,
    status: ActionStatus.idle,
    error: undefined
}

export enum PassValidationCompStatus {
  default="default",
  waiting="waiting",
  loading="loading",
  succeeded="succeeded",
  failed="failed"
}

export const loading = ({
    width = '100%',
    height = '2rem',
    m = 'mt-3',
    bg = 'bg-light',
    animation = true,
    columns = 1,
  } = {}) => html`
  ${ columns === 1 ? html `
    <div class="progress ${m}" style="width: ${width}; height: ${height}">
      <div class="${bg} ${animation ? 'progress-bar progress-bar-animated' : ''}"></div>
    </div>
  ` : html`
    <div style="display: grid; grid-template-columns: ${repeatThis(columns).times(() => '1fr ')}; grid-gap: 1rem;">
      ${ repeatThis(columns).times(() => html`
        <div class="progress ${m}" style="width: ${width}; height: ${height}">
          <div class="${bg} ${animation ? 'progress-bar progress-bar-animated' : ''}"></div>
        </div>
      `)}
    </div>
  `}
`;

export const spinner = ({ size = 'md', msg = 'Loading' } = {}) => html`
  <div class="spinner-border spinner-border-${size}" role="status">
      <span class="visually-hidden">${msg}</span>
  </div>
`;

export const smallSpinner = ({ msg = '' } = {}) => spinner({ size: 'sm', msg });

export const SHARE_CART_ID = "shareCartId";
export const TID = "tid";
export const REDIRECT_URL_HECLR_KEY = "redirectUrl";
export const GENERIC_TRANSACTION_ERROR = 'Sorry, we\'ve encountered a technical issue and cannot complete your transaction, please try again. ';
export const GENERIC_API_ERROR = 'That didn’t seem to work... We’re sorry for the inconvenience. Please try again, and if the problem persists reach out to customer support.';
export const PRICING_SUBJECT_INFO = 'Pricing subject to change at any time. We only ship to US locations.';
export const FA4_SUBSCRIPTION_BANNER_TEXT = (firstName: string) => `Hey ${firstName}, you don't have an active subscription to First Aid Forward. Please browse through our product options here to subscribe!`;
export const MISSING_FORM_FIELDS_ERROR = 'Please complete all required fields';

const ORDER_CONFIRMATION_ERROR: TemplateResult = html`<span>Sorry, we have been temporarily unable to process your payment at this time. Please retry with another payment option or contact our&nbsp;<a href="https://mh.my.site.com/CSOM/s/" target="_new">support team.</a></span>`;
const PAYMENT_METHODS_LOAD_ERROR: TemplateResult = html`<span>Sorry, something went wrong while loading this page. Try refreshing your browser or returning to the cart and checking out again. If the issue persists, contact our&nbsp;<a href="https://mh.my.site.com/CSOM/s/" target="_new">support team.</a></span>`;
const BRAIN_TREE_HOSTED_FIELDS_ERROR: TemplateResult = html `<span>We are unable to process payment and billing at this moment, try&nbsp;<a href='javascript:window.location.href=window.location.href'>refreshing.</a></span>`;
const PAYMENT_ERROR_1_MSG: TemplateResult = html`We're sorry, the payment information entered is not valid, please update the information or try another payment method.`;
const PAYMENT_ERROR_2_MSG: TemplateResult = html`We're sorry, this transaction was declined. Please reach out to your bank or payment method for more information, or try another payment method.`;
const PAYMENT_ERROR_3_MSG: TemplateResult = html`We're sorry, there has been an error. Please contact McGraw Hill&nbsp;<a href="https://mh.my.site.com/CXG/s/ContactUs">technical support</a>. Braintree setup issue.`;
const PAYMENT_ERROR_4_MSG: TemplateResult = html`We're sorry, this transaction was declined. Please reach out to your bank or payment method for more information, or try another payment method.`;
const PAYMENT_ERROR_5_MSG: TemplateResult = html`We're sorry, your country of residence must match the country of your payment method account.`;
const PAYMENT_ERROR_6_MSG: TemplateResult = html`We're sorry, the currency of the transaction must match the currency of your PayPal account.`;
const PAYMENT_ERROR_7_MSG: TemplateResult = html`We're sorry, this transaction was declined. Please reach out to your bank or payment method for more information, or try another payment method.`;
const PAYMENT_ERROR_8_MSG: TemplateResult = html`We're sorry, there was a problem processing this transaction. Please try again.`;
const RMS_ORDER_INSERT_ERROR_MSG: TemplateResult = html`<span>Sorry, we have been temporarily unable to process your payment at this time. Please retry with another payment option or contact our&nbsp;<a href="https://mh.my.site.com/CSOM/s/" target="_new">support team.</a></span>`;
const ORDER_CONFIRMATION_LOAD_FAILURE: TemplateResult = html`<span>Your order has been processed, but we're having some trouble showing your order details on this page. To verify your order, please check your email for a confirmation of your order, or view your Order History in your&nbsp;<a href=${environment().accountUrl}>McGraw Hill Account</a>.<br><br><span class="fw-bold">Connect Access</span> - If you purchased Connect, make sure to use the section URL provided by your instructor to access your course the first time.</span>`;
const CONNECT_REQUIRED_ACTION_MESSAGE: TemplateResult = html`<span><b>Action Required for Connect Access:</b> You must click complete registration and complete your details to access assigned course content in Connect.</span>`;
const TAX_CALCULATED_ON_SCHOOL_ADDRESS: TemplateResult = html`<span>Taxes are calculated based on your school's address, as the use of the items in your order is associated with your school. You can view or update your school information in&nbsp;<a href=${environment().accountUrl} target="_new">My Account.</a></span>`;
const PRICING_FOR_SCHOOL_ACCOUNTS: TemplateResult = html`<span id="school-pricing-info-alert">Pricing for school accounts will display in the cart once you &nbsp;<a class="fw-bold" href=${getLoginURLWithRedirectUrlToCurrentPage()} @click=${(e) => captureUserLoginFromAlertAnalytics(e, (e.target as Element))}>log in.</a></span>`

export enum MessageTemplateId {
  ORDER_CONFIRMATION_ERROR = 'ORDER_CONFIRMATION_ERROR',
  ORDER_CONFIRMATION_LOAD_FAILURE = 'ORDER_CONFIRMATION_LOAD_FAILURE',
  BRAIN_TREE_HOSTED_FIELDS_ERROR = 'BRAIN_TREE_HOSTED_FIELDS_ERROR',
  PAYMENT_METHODS_LOAD_ERROR = 'PAYMENT_METHODS_LOAD_ERROR',
  PAYMENT_ERROR_1 = 'PAYMENT_ERROR_1',
  PAYMENT_ERROR_2 = 'PAYMENT_ERROR_2',
  PAYMENT_ERROR_3 = 'PAYMENT_ERROR_3',
  PAYMENT_ERROR_4 = 'PAYMENT_ERROR_4',
  PAYMENT_ERROR_5 = 'PAYMENT_ERROR_5',
  PAYMENT_ERROR_6 = 'PAYMENT_ERROR_6',
  PAYMENT_ERROR_7 = 'PAYMENT_ERROR_7',
  PAYMENT_ERROR_8 = 'PAYMENT_ERROR_8',
  RMS_ORDER_INSERT_ERROR = 'RMS_ORDER_INSERT_ERROR',
  FREE_TRIAL_CONVERTED = 'FREE_TRIAL_CONVERTED',
  FREE_TRIAL_INTL_CONVERTED = 'FREE_TRIAL_INTL_CONVERTED',
  CONNECT_REQUIRED_ACTION_MESSAGE = 'CONNECT_REQUIRED_ACTION_MESSAGE',
  TAX_CALCULATED_ON_SCHOOL_ADDRESS = 'TAX_CALCULATED_ON_SCHOOL_ADDRESS',
  PRICING_FOR_SCHOOL_ACCOUNTS = 'PRICING_FOR_SCHOOL_ACCOUNTS',
}

export const messageTemplates: Record<MessageTemplateId, TemplateResult> = {
  [MessageTemplateId.ORDER_CONFIRMATION_ERROR]: ORDER_CONFIRMATION_ERROR,
  [MessageTemplateId.ORDER_CONFIRMATION_LOAD_FAILURE]: ORDER_CONFIRMATION_LOAD_FAILURE,
  [MessageTemplateId.BRAIN_TREE_HOSTED_FIELDS_ERROR]: BRAIN_TREE_HOSTED_FIELDS_ERROR,
  [MessageTemplateId.PAYMENT_METHODS_LOAD_ERROR]: PAYMENT_METHODS_LOAD_ERROR,
  [MessageTemplateId.PAYMENT_ERROR_1]: PAYMENT_ERROR_1_MSG,
  [MessageTemplateId.PAYMENT_ERROR_2]: PAYMENT_ERROR_2_MSG,
  [MessageTemplateId.PAYMENT_ERROR_3]: PAYMENT_ERROR_3_MSG,
  [MessageTemplateId.PAYMENT_ERROR_4]: PAYMENT_ERROR_4_MSG,
  [MessageTemplateId.PAYMENT_ERROR_5]: PAYMENT_ERROR_5_MSG,
  [MessageTemplateId.PAYMENT_ERROR_6]: PAYMENT_ERROR_6_MSG,
  [MessageTemplateId.PAYMENT_ERROR_7]: PAYMENT_ERROR_7_MSG,
  [MessageTemplateId.PAYMENT_ERROR_8]: PAYMENT_ERROR_8_MSG,
  [MessageTemplateId.RMS_ORDER_INSERT_ERROR]: RMS_ORDER_INSERT_ERROR_MSG,
  [MessageTemplateId.FREE_TRIAL_CONVERTED]: FREE_TRIAL_CONVERTED_MSG,
  [MessageTemplateId.FREE_TRIAL_INTL_CONVERTED]: FREE_TRIAL_INTL_CONVERTED_MSG,
  [MessageTemplateId.CONNECT_REQUIRED_ACTION_MESSAGE]: CONNECT_REQUIRED_ACTION_MESSAGE,
  [MessageTemplateId.TAX_CALCULATED_ON_SCHOOL_ADDRESS]: TAX_CALCULATED_ON_SCHOOL_ADDRESS,
  [MessageTemplateId.PRICING_FOR_SCHOOL_ACCOUNTS]: PRICING_FOR_SCHOOL_ACCOUNTS
};